import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import AuthForm from './AuthForm';
import { ActiveSessionInfo, login } from '../../services/authService';
import { consumeSessionEndedMessage } from '../../services/authSession';
import { TOKEN_KEY, USER_ROLE_KEY } from '../../utils/constants';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [pendingOverride, setPendingOverride] = React.useState<{
    credentials: { email: string; password: string };
    activeSession: ActiveSessionInfo | null;
  } | null>(null);

  React.useEffect(() => {
    const message = consumeSessionEndedMessage();
    if (message) {
      toast.info(message);
    }
  }, []);

  const finalizeLogin = React.useCallback((response: any) => {
    localStorage.setItem(TOKEN_KEY, response.token);
    const role = response.user?.role;
    if (role) {
      localStorage.setItem(USER_ROLE_KEY, role);
    } else {
      localStorage.removeItem(USER_ROLE_KEY);
    }

    if (role === 'ep') {
      navigate('/ep-dashboard');
    } else if (role === 'admin') {
      navigate('/admin-dashboard');
    } else if (role === 'parent') {
      navigate('/parent-dashboard');
    } else {
      navigate('/dashboard');
    }
  }, [navigate]);

  const executeLogin = React.useCallback(
    async (
      credentials: { email: string; password: string },
      forceLogoutExisting: boolean,
    ) => {
      const response = await login(credentials, {
        clientType: 'web',
        forceLogoutExisting,
        deviceName: 'Website',
      });
      finalizeLogin(response);
      setPendingOverride(null);
    },
    [finalizeLogin],
  );

  const handleLogin = async (data: { email: string; password: string }) => {
    try {
      await executeLogin(data, false);
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 409 &&
        error.response?.data?.code === 'SESSION_ALREADY_ACTIVE'
      ) {
        setPendingOverride({
          credentials: data,
          activeSession: error.response.data.activeSession ?? null,
        });
        return { completed: false };
      }

      throw new Error(
        axios.isAxiosError(error)
          ? error.response?.data?.message || error.message
          : 'Login failed',
      );
    }
  };

  const handleOverrideLogin = async () => {
    if (!pendingOverride) {
      return;
    }

    try {
      await executeLogin(pendingOverride.credentials, true);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Unable to continue with this login.',
      );
    }
  };

  const sessionStartedAt = pendingOverride?.activeSession?.startedAt
    ? new Date(pendingOverride.activeSession.startedAt).toLocaleString()
    : null;

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: 6,
          background:
            'radial-gradient(circle at top, rgba(90, 75, 252, 0.12), transparent 40%), radial-gradient(circle at bottom, rgba(0, 184, 148, 0.16), transparent 45%), #f4f6fb',
        }}
      >
        <AuthForm isLogin onSubmit={handleLogin} />
      </Box>

      <Dialog
        open={Boolean(pendingOverride)}
        onClose={() => setPendingOverride(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Website session already active</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This account is already logged in on another website session. If you continue,
            the earlier website session will be logged out automatically.
          </DialogContentText>
          {pendingOverride?.activeSession && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Active on: {pendingOverride.activeSession.deviceLabel}
              </Typography>
              {sessionStartedAt && (
                <Typography variant="body2" color="text.secondary">
                  Started: {sessionStartedAt}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingOverride(null)}>Cancel</Button>
          <Button onClick={handleOverrideLogin} variant="contained">
            Continue Login
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Login;
